import {type ScatterAxisKey} from '../axisKeyTypes';
import {type ScatterPlotConfig} from '../common';
import {type SemanticColorsConfigResult} from '../semanticColors/effectiveSemanticColorsConfig';
import {
  type PersistedSemanticColorsSettings,
  RunColorOptions,
  YAxisValues,
} from '../semanticColors/types';
import {
  isMeaningfulAxisKey,
  isMeaningfulSemanticKey,
  parseColorFieldFromSemanticKey,
  toAxisKeyString,
} from './scatterColorKeys';

export enum ScatterColorType {
  Discrete = 'discrete',
  Continuous = 'continuous',
  Gradient = 'gradient',
}

export function getScatterPanelColoringMode(
  config: Pick<ScatterPlotConfig, 'semanticLegendSettings' | 'zAxis'>
): ScatterColorType | undefined {
  const semanticColorType = getSemanticSettingsColorType(
    config.semanticLegendSettings
  );
  if (semanticColorType !== undefined) {
    return semanticColorType;
  }
  if (isMeaningfulAxisKey(config.zAxis)) {
    return ScatterColorType.Gradient;
  }
  return undefined;
}

export function getScatterPanelColorField(
  config: Pick<ScatterPlotConfig, 'semanticLegendSettings' | 'zAxis'>
): string | undefined {
  const semanticAxisKey = getSemanticSettingsAxisKey(
    config.semanticLegendSettings
  );
  if (semanticAxisKey != null) {
    return semanticAxisKey;
  }
  if (isMeaningfulAxisKey(config.zAxis)) {
    return config.zAxis;
  }
  return undefined;
}

export function getColorAxisKeyForDisplay({
  config,
  semanticColorsConfig,
}: {
  config: Pick<ScatterPlotConfig, 'semanticLegendSettings' | 'zAxis'>;
  semanticColorsConfig: SemanticColorsConfigResult;
}): ScatterAxisKey | undefined {
  const panelSemanticAxisKey = getSemanticSettingsAxisKey(
    config.semanticLegendSettings
  );
  if (panelSemanticAxisKey != null) {
    return panelSemanticAxisKey;
  }

  if (isMeaningfulAxisKey(config.zAxis)) {
    return config.zAxis;
  }

  if (semanticColorsConfig.isActive) {
    return getSemanticSettingsAxisKey(semanticColorsConfig.persisted);
  }

  return undefined;
}

export function getColorTypeForDisplay({
  config,
  semanticColorsConfig,
}: {
  config: Pick<ScatterPlotConfig, 'semanticLegendSettings' | 'zAxis'>;
  semanticColorsConfig: SemanticColorsConfigResult;
}): ScatterColorType | undefined {
  const panelSemanticColorType = getSemanticSettingsColorType(
    config.semanticLegendSettings
  );
  if (panelSemanticColorType != null) {
    return panelSemanticColorType;
  }

  if (isMeaningfulAxisKey(config.zAxis)) {
    return ScatterColorType.Gradient;
  }

  if (semanticColorsConfig.isActive) {
    return getSemanticSettingsColorType(semanticColorsConfig.persisted);
  }

  return undefined;
}

function getSemanticSettingsAxisKey(
  settings: PersistedSemanticColorsSettings | undefined
): ScatterAxisKey | undefined {
  if (
    settings == null ||
    settings.runColorOptions === RunColorOptions.OriginalProjectColors ||
    !isMeaningfulSemanticKey(settings.key)
  ) {
    return undefined;
  }
  const parsed = parseColorFieldFromSemanticKey(settings.key);
  return parsed == null ? undefined : toAxisKeyString(parsed);
}

function getSemanticSettingsColorType(
  settings: PersistedSemanticColorsSettings | undefined
): ScatterColorType | undefined {
  if (
    settings == null ||
    settings.runColorOptions === RunColorOptions.OriginalProjectColors ||
    !isMeaningfulSemanticKey(settings.key)
  ) {
    return undefined;
  }
  return settings.yAxisValue === YAxisValues.Config_Categories
    ? ScatterColorType.Discrete
    : ScatterColorType.Continuous;
}
